// React Query
import {
  UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

// Utils
import { postRequest as post } from '../utils/HttpMethods';
import * as Sentry from '@sentry/react';
import { claimQueryKeys, submissionQueryKeys } from 'api/resource';
import { PayorEnum } from 'interfaces';

interface SubmissionResponseBody {
  fileName: string;
  report: string;
}

interface SubmissionResponse {
  status: number;
  message: string;
  response: SubmissionResponseBody;
}

const generateSubmissionFile = async (submissionID: string): Promise<any> => {
  const submission = await post('QBilling', `/action/generate/submission`, {
    submissionID,
  });
  if (!submission) {
    throw new Error('No response body');
  }
  try {
    const parsedSubmission = await submission;
    return parsedSubmission;
  } catch (error) {
    throw new Error('Error parsing response');
  }
};

export const useGenerateSubmissionFile = (): UseMutationResult<string> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: generateSubmissionFile,

    onError: (error) => {
      Sentry.captureException(error);
      queryClient.invalidateQueries({
        queryKey: [...claimQueryKeys.all, ...submissionQueryKeys.all],
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [...claimQueryKeys.all, ...submissionQueryKeys.all],
      });
      return data;
    },

    retry: 0,
  });
};

const submitToHealthAuthority = async ({
  submissionID,
  payor,
}: {
  submissionID: string;
  payor: PayorEnum;
}): Promise<SubmissionResponse> => {
  try {
    const response: SubmissionResponse = await post(
      'QBilling',
      `/action/submit/${payor.toLowerCase()}`,
      {
        body: {
          submissionID,
        },
      }
    );

    return response;
  } catch (error) {
    Sentry.setContext('payload', { submissionID, payor });
    Sentry.captureException('Failed to submit to health authority');
  }
};

export const useSubmitToHealthAuthority =
  (): UseMutationResult<SubmissionResponse> => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: submitToHealthAuthority,
      onError: (error) => {
        Sentry.captureException(error);
        queryClient.invalidateQueries({
          queryKey: [...claimQueryKeys.all, ...submissionQueryKeys.all],
        });
      },
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: [...claimQueryKeys.all, ...submissionQueryKeys.all],
        });
        return data;
      },

      retry: 0,
    });
  };
